import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    IconMoodSad, IconExternalLink, IconLoader2, IconRefresh, IconPhoto, IconX,
    IconWand, IconCircleCheck, IconClockHour4, IconRotateClockwise, IconBan, IconEye,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import aiTemplateService from '../../../services/aiTemplateService';
import SelectField from '../../../components/common/SelectField';
import ConfirmDialog from '../../../components/common/ConfirmDialog';

const PAGE_LIMIT = 30;

export default function AdminNegativeFeedbackPage() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const locale = isBn ? 'bn-BD' : 'en-US';

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [preview, setPreview] = useState(null); // { url, original }
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'pending' | 'solved' | 'rejected'
    const [sortOrder, setSortOrder] = useState('newest');    // 'newest' | 'oldest'
    const [templateFilter, setTemplateFilter] = useState('all'); // 'all' | template id (string)
    const [templates, setTemplates] = useState([]); // [{ id, name, nameBn, thumbnail, count }]
    const [counts, setCounts] = useState({ all: 0, pending: 0, solved: 0, rejected: 0 });
    const [reopeningId, setReopeningId] = useState(null);
    const [rejectingId, setRejectingId] = useState(null);
    // Reopen needs an "are you sure" step — { row, unreject } or null.
    const [reopenConfirm, setReopenConfirm] = useState(null);

    const reasonLabel = (key) => key ? t(`worldCupJersey.feedback.reasons.${key}`, { defaultValue: key }) : '—';
    const tplName = (tpl) => tpl ? ((isBn && tpl.nameBn) ? tpl.nameBn : tpl.name) : null;
    const fmtDate = (d) => d ? new Date(d).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

    const fetchPage = useCallback(async (pageNum, append) => {
        append ? setLoadingMore(true) : setLoading(true);
        try {
            const params = { page: pageNum, limit: PAGE_LIMIT, sort: sortOrder };
            if (statusFilter !== 'all') params.status = statusFilter;
            if (templateFilter !== 'all') params.aiTemplateId = Number(templateFilter);
            const res = await aiTemplateService.negativeFeedback(params);
            const data = res?.data || {};
            setItems(prev => append ? [...prev, ...(data.items || [])] : (data.items || []));
            setTotal(data.total || 0);
            setHasMore(!!data.hasMore);
            setPage(pageNum);
            if (data.counts) setCounts(data.counts);
            if (Array.isArray(data.templates)) setTemplates(data.templates);
        } catch {
            notifications.show({ title: t('aiTemplateFeedback.error'), message: t('aiTemplateFeedback.failedToLoad'), color: 'red' });
        } finally {
            append ? setLoadingMore(false) : setLoading(false);
        }
    }, [t, statusFilter, sortOrder, templateFilter]);

    // Refetch from page 1 whenever any filter changes.
    useEffect(() => { fetchPage(1, false); }, [fetchPage]);

    const fmt = (n) => (n ?? 0).toLocaleString(locale);

    // Open the template's edit page in "resolve" mode, passing the issue so the AI
    // prompt re-analysis there can target this specific complaint.
    const handleResolve = (row) => {
        if (!row.aiTemplate?.id) return;
        navigate(`/admin/ai-templates/${row.aiTemplate.id}/edit?resolveFeedback=${row.id}`, {
            state: {
                resolveFeedback: {
                    id: row.id,
                    reason: reasonLabel(row.feedbackReason),
                    note: row.feedbackNote || '',
                    aiImagePath: row.aiImagePath || null,
                    uploadedImagePath: row.uploadedImagePath || null,
                },
            },
        });
    };

    const handleReopen = async (row) => {
        setReopeningId(row.id);
        try {
            await aiTemplateService.resolveNegativeFeedback(row.id, false);
            fetchPage(1, false);
        } catch {
            notifications.show({ title: t('aiTemplateFeedback.error'), message: t('aiTemplateFeedback.failedToLoad'), color: 'red' });
        } finally {
            setReopeningId(null);
        }
    };

    // Reject = admin decides this report won't be fixed; rejected=false re-opens it.
    const handleReject = async (row, rejected = true) => {
        setRejectingId(row.id);
        try {
            await aiTemplateService.rejectNegativeFeedback(row.id, rejected);
            fetchPage(1, false);
        } catch {
            notifications.show({ title: t('aiTemplateFeedback.error'), message: t('aiTemplateFeedback.failedToLoad'), color: 'red' });
        } finally {
            setRejectingId(null);
        }
    };

    // Confirmed via the dialog: un-reject a rejected report, or reopen a solved one.
    const confirmReopen = () => {
        if (!reopenConfirm) return;
        const { row, unreject } = reopenConfirm;
        setReopenConfirm(null);
        if (unreject) handleReject(row, false);
        else handleReopen(row);
    };

    const StatusTab = ({ value, label, count }) => (
        <button
            onClick={() => setStatusFilter(value)}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] text-[13px] font-semibold transition-colors ${
                statusFilter === value
                    ? 'bg-[#E2702E] text-white'
                    : 'bg-white dark:bg-zinc-900 border border-[#E9E6E0] dark:border-zinc-700 text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30'
            }`}
        >
            {label}
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${statusFilter === value ? 'bg-white/25' : 'bg-[#F1EFEA] dark:bg-zinc-800'}`}>{fmt(count)}</span>
        </button>
    );

    return (
        <div className="font-hind w-full py-4 md:py-6">
            {/* Header */}
            <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold tracking-[-0.01em] text-[#1C1B19] dark:text-zinc-100">
                        {t('aiTemplateFeedback.pageTitle')}
                    </h1>
                    <p className="mt-1 text-sm text-[#6E6A63] dark:text-zinc-400">{t('aiTemplateFeedback.pageSubtitle')}</p>
                </div>
                <button
                    onClick={() => fetchPage(1, false)}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-[10px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors disabled:opacity-50 flex-shrink-0"
                >
                    <IconRefresh size={15} className={loading ? 'animate-spin text-[#E2702E]' : 'text-[#E2702E]'} /> {t('aiTemplateFeedback.refresh')}
                </button>
            </div>

            {/* Filters: status tabs + sort + template */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <StatusTab value="all" label={t('aiTemplateFeedback.filterAll')} count={counts.all} />
                <StatusTab value="pending" label={t('aiTemplateFeedback.statusPending')} count={counts.pending} />
                <StatusTab value="solved" label={t('aiTemplateFeedback.statusSolved')} count={counts.solved} />
                <StatusTab value="rejected" label={t('aiTemplateFeedback.statusRejected')} count={counts.rejected} />
                <div className="ml-auto flex items-center gap-2 flex-wrap">
                    <div style={{ flex: '0 0 140px' }}>
                        <SelectField
                            value={sortOrder}
                            onChange={setSortOrder}
                            options={[
                                { value: 'newest', label: t('aiTemplateFeedback.sortNewest') },
                                { value: 'oldest', label: t('aiTemplateFeedback.sortOldest') },
                            ]}
                        />
                    </div>
                    <div style={{ flex: '0 0 200px' }}>
                        <SelectField
                            value={templateFilter}
                            onChange={setTemplateFilter}
                            options={[
                                { value: 'all', label: t('aiTemplateFeedback.allTemplates') },
                                ...templates.map(tp => ({
                                    value: String(tp.id),
                                    label: `${(isBn && tp.nameBn) ? tp.nameBn : tp.name} (${fmt(tp.count)})`,
                                })),
                            ]}
                        />
                    </div>
                </div>
            </div>

            {/* Skeleton — mirrors the real table layout so the swap doesn't jump */}
            {loading && (
                <div className="rounded-2xl border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden animate-pulse">
                    <div className="h-4 my-2.5 mx-3 flex items-center gap-6 bg-[#FAF9F6] dark:bg-zinc-800/60 rounded" />
                    {Array.from({ length: 7 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-4 px-3 py-2 border-t border-[#F1EFEA] dark:border-zinc-800">
                            <div className="w-10 h-10 rounded-lg bg-[#F0EFEC] dark:bg-zinc-800 flex-shrink-0" />
                            <div className="h-5 w-28 rounded-full bg-[#F0EFEC] dark:bg-zinc-800 flex-shrink-0" />
                            <div className="flex-1 min-w-0 space-y-1.5">
                                <div className="h-3 w-3/5 rounded bg-[#F0EFEC] dark:bg-zinc-800" />
                                <div className="h-2.5 w-2/5 rounded bg-[#F0EFEC] dark:bg-zinc-800" />
                            </div>
                            <div className="hidden sm:block h-3 w-32 rounded bg-[#F0EFEC] dark:bg-zinc-800 flex-shrink-0" />
                            <div className="hidden md:block h-5 w-36 rounded bg-[#F0EFEC] dark:bg-zinc-800 flex-shrink-0" />
                            <div className="hidden md:block h-5 w-20 rounded-full bg-[#F0EFEC] dark:bg-zinc-800 flex-shrink-0" />
                            <div className="h-8 w-28 rounded-lg bg-[#F0EFEC] dark:bg-zinc-800 flex-shrink-0" />
                        </div>
                    ))}
                </div>
            )}

            {/* Empty */}
            {!loading && items.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-20">
                    <div className="w-16 h-16 rounded-2xl bg-[#F1EFEA] dark:bg-zinc-800 flex items-center justify-center">
                        <IconMoodSad size={28} className="text-[#C3BEB4] dark:text-zinc-600" />
                    </div>
                    <p className="text-sm text-[#6E6A63] dark:text-zinc-400">{t('aiTemplateFeedback.empty')}</p>
                </div>
            )}

            {/* Table */}
            {!loading && items.length > 0 && (
                <>
                    <p className="text-[13px] text-[#6E6A63] dark:text-zinc-400 mb-3">{t('aiTemplateFeedback.count', { num: fmt(total) })}</p>
                    <div className="rounded-2xl border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[960px]">
                            <thead>
                                <tr className="bg-[#FAF9F6] dark:bg-zinc-800/60 text-[11px] font-semibold uppercase tracking-wide text-[#9C988F] dark:text-zinc-400">
                                    <th className="px-3 py-2.5 font-semibold">{t('aiTemplateFeedback.colImage')}</th>
                                    <th className="px-3 py-2.5 font-semibold">{t('aiTemplateFeedback.colReason')}</th>
                                    <th className="px-3 py-2.5 font-semibold">{t('aiTemplateFeedback.colNote')}</th>
                                    <th className="px-3 py-2.5 font-semibold">{t('aiTemplateFeedback.colUser')}</th>
                                    <th className="px-3 py-2.5 font-semibold">{t('aiTemplateFeedback.colDate')}</th>
                                    <th className="px-3 py-2.5 font-semibold">{t('aiTemplateFeedback.colTemplate')}</th>
                                    <th className="px-3 py-2.5 font-semibold">{t('aiTemplateFeedback.colStatus')}</th>
                                    <th className="px-3 py-2.5 font-semibold text-right">{t('aiTemplateFeedback.colAction')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => (
                                    <tr key={row.id} className="border-t border-[#F1EFEA] dark:border-zinc-800 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800/40 transition-colors align-middle">
                                        {/* Generated image */}
                                        <td className="px-3 py-2">
                                            {row.aiImagePath ? (
                                                <button
                                                    onClick={() => setPreview({ url: row.aiImagePath, original: row.uploadedImagePath })}
                                                    className="block w-10 h-10 rounded-lg overflow-hidden border border-[#ECE9E3] dark:border-zinc-700 bg-[#F0EFEC] dark:bg-zinc-800"
                                                    title={t('aiTemplateFeedback.viewImage')}
                                                >
                                                    <img src={row.aiImagePath} alt="" loading="lazy" className="w-full h-full object-cover" />
                                                </button>
                                            ) : (
                                                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#F1EFEA] dark:bg-zinc-800 text-[#C3BEB4] dark:text-zinc-600">
                                                    <IconPhoto size={16} />
                                                </div>
                                            )}
                                        </td>
                                        {/* Reason */}
                                        <td className="px-3 py-2">
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-500/10 text-red-600 dark:text-red-400 whitespace-nowrap">
                                                {reasonLabel(row.feedbackReason)}
                                            </span>
                                        </td>
                                        {/* Note */}
                                        <td className="px-3 py-2 max-w-[240px]">
                                            <p className="text-[13px] text-[#1C1B19] dark:text-zinc-200 whitespace-pre-wrap break-words">
                                                {row.feedbackNote || <span className="text-[#C3BEB4] dark:text-zinc-600">—</span>}
                                            </p>
                                        </td>
                                        {/* User */}
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <p className="text-[13px] font-medium text-[#1C1B19] dark:text-zinc-200 truncate max-w-[140px]">{row.user?.name || '—'}</p>
                                            {row.user?.phone && <p className="text-[11px] text-[#9C988F] dark:text-zinc-500">{row.user.phone}</p>}
                                        </td>
                                        {/* Date */}
                                        <td className="px-3 py-2 whitespace-nowrap text-[12.5px] text-[#6E6A63] dark:text-zinc-400">
                                            {fmtDate(row.feedbackAt || row.createdAt)}
                                        </td>
                                        {/* Template → edit page */}
                                        <td className="px-3 py-2">
                                            {row.aiTemplate ? (
                                                <button
                                                    onClick={() => navigate(`/admin/ai-templates/${row.aiTemplate.id}/edit`)}
                                                    className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#E2702E] hover:underline"
                                                    title={t('aiTemplateFeedback.openTemplate')}
                                                >
                                                    {row.aiTemplate.thumbnail && (
                                                        <img src={row.aiTemplate.thumbnail} alt="" className="w-6 h-6 rounded object-cover border border-[#ECE9E3] dark:border-zinc-700" />
                                                    )}
                                                    <span className="truncate max-w-[150px]">{tplName(row.aiTemplate)}</span>
                                                    <IconExternalLink size={13} className="flex-shrink-0" />
                                                </button>
                                            ) : (
                                                <span className="text-[13px] text-[#C3BEB4] dark:text-zinc-600">{t('aiTemplateFeedback.noTemplate')}</span>
                                            )}
                                        </td>
                                        {/* Status */}
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {row.status === 'rejected' ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-500/10 text-red-500 dark:text-red-400">
                                                    <IconBan size={13} /> {t('aiTemplateFeedback.statusRejected')}
                                                </span>
                                            ) : row.status === 'solved' ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                                    <IconCircleCheck size={13} /> {t('aiTemplateFeedback.statusSolved')}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-500/10 text-amber-600 dark:text-amber-400">
                                                    <IconClockHour4 size={13} /> {t('aiTemplateFeedback.statusPending')}
                                                </span>
                                            )}
                                        </td>
                                        {/* Action */}
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            {(row.aiTemplate || row.aiImagePath) ? (
                                                <div className="inline-flex items-center gap-1.5">
                                                    {row.aiImagePath && (
                                                        <button
                                                            onClick={() => setPreview({ url: row.aiImagePath, original: row.uploadedImagePath })}
                                                            title={t('aiTemplateFeedback.preview')}
                                                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#E9E6E0] dark:border-zinc-700 text-[12.5px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800 transition-colors"
                                                        >
                                                            <IconEye size={13} /> {t('aiTemplateFeedback.preview')}
                                                        </button>
                                                    )}
                                                    {/* Rejected rows: only offer to reopen — no resolve button */}
                                                    {row.aiTemplate && row.status === 'rejected' && (
                                                        <button
                                                            onClick={() => setReopenConfirm({ row, unreject: true })}
                                                            disabled={rejectingId === row.id}
                                                            title={t('aiTemplateFeedback.reopen')}
                                                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#E9E6E0] dark:border-zinc-700 text-[12.5px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
                                                        >
                                                            {rejectingId === row.id ? <IconLoader2 size={13} className="animate-spin" /> : <IconRotateClockwise size={13} />}
                                                            {t('aiTemplateFeedback.reopen')}
                                                        </button>
                                                    )}
                                                    {row.aiTemplate && row.status !== 'rejected' && (
                                                        <>
                                                            {row.status === 'solved' && (
                                                                <button
                                                                    onClick={() => setReopenConfirm({ row, unreject: false })}
                                                                    disabled={reopeningId === row.id}
                                                                    title={t('aiTemplateFeedback.reopen')}
                                                                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#E9E6E0] dark:border-zinc-700 text-[12.5px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FAF9F6] dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
                                                                >
                                                                    {reopeningId === row.id ? <IconLoader2 size={13} className="animate-spin" /> : <IconRotateClockwise size={13} />}
                                                                    {t('aiTemplateFeedback.reopen')}
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleReject(row)}
                                                                disabled={rejectingId === row.id}
                                                                title={t('aiTemplateFeedback.rejectHint')}
                                                                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-red-200 dark:border-red-900/40 text-[12.5px] font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                                                            >
                                                                {rejectingId === row.id ? <IconLoader2 size={13} className="animate-spin" /> : <IconBan size={13} />}
                                                                {t('aiTemplateFeedback.reject')}
                                                            </button>
                                                            <button
                                                                onClick={() => handleResolve(row)}
                                                                title={t('aiTemplateFeedback.resolveHint')}
                                                                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#E2702E] hover:bg-[#E2702E]/90 text-white text-[12.5px] font-semibold transition-colors"
                                                            >
                                                                <IconWand size={14} /> {row.status === 'solved' ? t('aiTemplateFeedback.fixAgain') : t('aiTemplateFeedback.resolve')}
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-[12.5px] text-[#C3BEB4] dark:text-zinc-600">—</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {hasMore && (
                        <div className="flex justify-center mt-5">
                            <button
                                onClick={() => fetchPage(page + 1, true)}
                                disabled={loadingMore}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[10px] border border-[#E9E6E0] dark:border-zinc-700 bg-white dark:bg-zinc-900 text-[13px] font-semibold text-[#6E6A63] dark:text-zinc-300 hover:bg-[#FCEFE7] dark:hover:bg-orange-950/30 transition-colors disabled:opacity-50"
                            >
                                {loadingMore && <IconLoader2 size={16} className="animate-spin text-[#E2702E]" />}
                                {t('aiTemplateFeedback.loadMore')}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Reopen confirmation */}
            <ConfirmDialog
                open={!!reopenConfirm}
                onClose={() => setReopenConfirm(null)}
                onConfirm={confirmReopen}
                variant="primary"
                title={t('aiTemplateFeedback.reopenConfirmTitle')}
                description={t('aiTemplateFeedback.reopenConfirmDesc')}
                confirmLabel={t('aiTemplateFeedback.reopen')}
            />

            {/* Lightweight image lightbox — generated result + original upload side by side */}
            {preview && (
                <div
                    className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setPreview(null)}
                >
                    <button
                        onClick={() => setPreview(null)}
                        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors z-10"
                        aria-label={t('common.close')}
                    >
                        <IconX size={20} />
                    </button>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-full max-h-[90vh]"
                    >
                        {preview.original && (
                            <figure className="flex flex-col items-center gap-2 min-h-0">
                                <img src={preview.original} alt="" className="max-w-full max-h-[76vh] rounded-lg object-contain" />
                                <figcaption className="text-[12px] font-medium text-white/80">{t('aiTemplateFeedback.originalImage')}</figcaption>
                            </figure>
                        )}
                        <figure className="flex flex-col items-center gap-2 min-h-0">
                            <img src={preview.url} alt="" className="max-w-full max-h-[76vh] rounded-lg object-contain" />
                            <figcaption className="text-[12px] font-medium text-white/80">{t('aiTemplateFeedback.generatedImage')}</figcaption>
                        </figure>
                    </div>
                </div>
            )}
        </div>
    );
}
